import { supabase } from "../supabaseClient";

/**
 * Get all friends for a user from the friendships table
 * @param {string} userId - The user's public_user_id
 * @returns {Promise<Array>} Array of friend user objects
 */
export const getFriends = async (userId) => {
  if (!userId) {
    console.error("getFriends: userId is required");
    return [];
  }

  try {
    // Query friendships where user is either user_id_1 or user_id_2
    const { data: friendships, error: friendshipsError } = await supabase
      .from("friendships")
      .select("user_id_1, user_id_2")
      .or(`user_id_1.eq.${userId},user_id_2.eq.${userId}`);

    if (friendshipsError) {
      console.error("Error fetching friendships:", friendshipsError);
      return [];
    }

    if (!friendships || friendships.length === 0) {
      return [];
    }

    // Extract friend IDs (the one that's NOT the current user)
    const friendIds = friendships.map((friendship) =>
      friendship.user_id_1 === userId
        ? friendship.user_id_2
        : friendship.user_id_1
    );

    // Fetch user details for all friends
    const { data: friends, error: friendsError } = await supabase
      .from("users")
      .select("public_user_id, username, email, avatar, favorite_team, about_me, name_color")
      .in("public_user_id", friendIds);

    if (friendsError) {
      console.error("Error fetching friend details:", friendsError);
      return [];
    }

    return friends || [];
  } catch (error) {
    console.error("Unexpected error in getFriends:", error);
    return [];
  }
};

/**
 * Check if two users are friends
 * @param {string} userId1 - First user's public_user_id
 * @param {string} userId2 - Second user's public_user_id
 * @returns {Promise<boolean>} True if they are friends
 */
export const areFriends = async (userId1, userId2) => {
  if (!userId1 || !userId2) return false;

  try {
    const { data, error } = await supabase
      .from("friendships")
      .select("id")
      .or(
        `and(user_id_1.eq.${userId1},user_id_2.eq.${userId2}),and(user_id_1.eq.${userId2},user_id_2.eq.${userId1})`
      )
      .limit(1);

    if (error) {
      console.error("Error checking friendship:", error);
      return false;
    }

    return data && data.length > 0;
  } catch (error) {
    console.error("Unexpected error in areFriends:", error);
    return false;
  }
};

/**
 * Send a friend request
 * @param {number} fromUserId - Sender's user.id (not public_user_id)
 * @param {number} toUserId - Recipient's user.id (not public_user_id)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendFriendRequest = async (fromUserId, toUserId) => {
  if (!fromUserId || !toUserId) {
    return { success: false, error: "User IDs are required" };
  }

  if (fromUserId === toUserId) {
    return { success: false, error: "Cannot send friend request to yourself" };
  }

  try {
    // Check if friendship already exists
    const { data: currentUser } = await supabase
      .from("users")
      .select("public_user_id")
      .eq("id", fromUserId)
      .single();

    const { data: targetUser } = await supabase
      .from("users")
      .select("public_user_id")
      .eq("id", toUserId)
      .single();

    if (!currentUser || !targetUser) {
      return { success: false, error: "User not found" };
    }

    const friendshipExists = await areFriends(
      currentUser.public_user_id,
      targetUser.public_user_id
    );

    if (friendshipExists) {
      return { success: false, error: "You are already friends" };
    }

    // Check if request already exists
    const { data: existingRequest } = await supabase
      .from("friend_requests")
      .select("id, status")
      .or(`and(from_user.eq.${fromUserId},to_user.eq.${toUserId}),and(from_user.eq.${toUserId},to_user.eq.${fromUserId})`)
      .limit(1)
      .single();

    if (existingRequest) {
      if (existingRequest.status === "pending") {
        return { success: false, error: "Friend request already pending" };
      }
    }

    // Send the friend request
    const { error } = await supabase.from("friend_requests").insert({
      from_user: fromUserId,
      to_user: toUserId,
      status: "pending",
    });

    if (error) {
      console.error("Error sending friend request:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error("Unexpected error in sendFriendRequest:", error);
    return { success: false, error: "An unexpected error occurred" };
  }
};

/**
 * Accept a friend request and create friendship
 * @param {number} requestId - The friend_request.id
 * @param {number} acceptingUserId - The user.id of the person accepting (should be to_user)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const acceptFriendRequest = async (requestId, acceptingUserId) => {
  if (!requestId || !acceptingUserId) {
    return { success: false, error: "Request ID and user ID are required" };
  }

  try {
    // Fetch the friend request
    const { data: request, error: requestError } = await supabase
      .from("friend_requests")
      .select("*")
      .eq("id", requestId)
      .single();

    if (requestError || !request) {
      console.error("Error fetching friend request:", requestError);
      return { success: false, error: "Friend request not found" };
    }

    // Verify the accepting user is the recipient
    if (request.to_user !== acceptingUserId) {
      return { success: false, error: "You are not authorized to accept this request" };
    }

    // Verify request is still pending
    if (request.status !== "pending") {
      return { success: false, error: "This request has already been processed" };
    }

    // Get public_user_ids for both users
    const { data: fromUser } = await supabase
      .from("users")
      .select("public_user_id")
      .eq("id", request.from_user)
      .single();

    const { data: toUser } = await supabase
      .from("users")
      .select("public_user_id")
      .eq("id", request.to_user)
      .single();

    if (!fromUser || !toUser) {
      return { success: false, error: "User not found" };
    }

    // Create the friendship
    const { error: friendshipError } = await supabase
      .from("friendships")
      .insert({
        user_id_1: fromUser.public_user_id < toUser.public_user_id 
          ? fromUser.public_user_id 
          : toUser.public_user_id,
        user_id_2: fromUser.public_user_id < toUser.public_user_id 
          ? toUser.public_user_id 
          : fromUser.public_user_id,
      });

    if (friendshipError) {
      console.error("Error creating friendship:", friendshipError);
      return { success: false, error: "Failed to create friendship" };
    }

    // Update the friend request status
    const { error: updateError } = await supabase
      .from("friend_requests")
      .update({ status: "accepted" })
      .eq("id", requestId);

    if (updateError) {
      console.error("Error updating friend request:", updateError);
      // Friendship was created, so still return success
    }

    return { success: true };
  } catch (error) {
    console.error("Unexpected error in acceptFriendRequest:", error);
    return { success: false, error: "An unexpected error occurred" };
  }
};

/**
 * Reject a friend request
 * @param {number} requestId - The friend_request.id
 * @param {number} rejectingUserId - The user.id of the person rejecting
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const rejectFriendRequest = async (requestId, rejectingUserId) => {
  if (!requestId || !rejectingUserId) {
    return { success: false, error: "Request ID and user ID are required" };
  }

  try {
    // Fetch the friend request
    const { data: request, error: requestError } = await supabase
      .from("friend_requests")
      .select("*")
      .eq("id", requestId)
      .single();

    if (requestError || !request) {
      return { success: false, error: "Friend request not found" };
    }

    // Verify the rejecting user is the recipient
    if (request.to_user !== rejectingUserId) {
      return { success: false, error: "You are not authorized to reject this request" };
    }

    // Update the request status
    const { error: updateError } = await supabase
      .from("friend_requests")
      .update({ status: "rejected" })
      .eq("id", requestId);

    if (updateError) {
      console.error("Error rejecting friend request:", updateError);
      return { success: false, error: "Failed to reject request" };
    }

    return { success: true };
  } catch (error) {
    console.error("Unexpected error in rejectFriendRequest:", error);
    return { success: false, error: "An unexpected error occurred" };
  }
};

/**
 * Remove a friend (unfriend)
 * @param {string} currentUserId - Current user's public_user_id
 * @param {string} friendId - Friend's public_user_id
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const removeFriend = async (currentUserId, friendId) => {
  if (!currentUserId || !friendId) {
    return { success: false, error: "User IDs are required" };
  }

  try {
    // Delete the friendship (RLS ensures user can only delete their own friendships)
    const { error } = await supabase
      .from("friendships")
      .delete()
      .or(
        `and(user_id_1.eq.${currentUserId},user_id_2.eq.${friendId}),and(user_id_1.eq.${friendId},user_id_2.eq.${currentUserId})`
      );

    if (error) {
      console.error("Error removing friend:", error);
      return { success: false, error: "Failed to remove friend" };
    }

    return { success: true };
  } catch (error) {
    console.error("Unexpected error in removeFriend:", error);
    return { success: false, error: "An unexpected error occurred" };
  }
};

/**
 * Get pending friend requests for a user
 * @param {number} userId - The user's id (not public_user_id)
 * @returns {Promise<Array>} Array of friend requests with user details
 */
export const getPendingFriendRequests = async (userId) => {
  if (!userId) {
    console.error("getPendingFriendRequests: userId is required");
    return [];
  }

  try {
    // Get all pending requests where user is the recipient
    const { data: requests, error: requestsError } = await supabase
      .from("friend_requests")
      .select(`
        id,
        from_user,
        to_user,
        status,
        created_at,
        sender:from_user(public_user_id, username, email, avatar)
      `)
      .eq("to_user", userId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (requestsError) {
      console.error("Error fetching friend requests:", requestsError);
      return [];
    }

    return requests || [];
  } catch (error) {
    console.error("Unexpected error in getPendingFriendRequests:", error);
    return [];
  }
};
